import { useState, useCallback } from 'react'
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import type {
  BranchableLevelConfig,
  ComponentConfigV2,
  HierarchyLevel,
} from '@/types/configurationV2'
import BranchSelector from './BranchSelector'
import ComponentCard from './ComponentCard'
import ComponentAdder from './ComponentAdder'
import OfflineMaterialsSettings from './OfflineMaterialsSettings'
import { createDefaultOfflineMaterials } from './offlineMaterials'

interface LevelTabProps {
  level: string
  config: BranchableLevelConfig
  onChange: (config: BranchableLevelConfig) => void
  allowedBranchLevels?: HierarchyLevel[]
  renderOptionsEditor?: (
    component: ComponentConfigV2,
    onUpdate: (updated: ComponentConfigV2) => void
  ) => React.ReactNode
}

export default function LevelTab({
  level,
  config,
  onChange,
  allowedBranchLevels,
  renderOptionsEditor,
}: LevelTabProps) {
  const safeConfig: BranchableLevelConfig = config ?? { belongsTo: null, default: { pre: [], post: [] }, overrides: [] }
  const [activeBranch, setActiveBranch] = useState<number | 'default'>('default')

  const getActiveConfig = () => {
    if (activeBranch === 'default') return safeConfig.default ?? { pre: [], post: [] }
    const override = (safeConfig.overrides ?? []).find((o) => o.contentId === activeBranch)
    return {
      pre: override?.pre ?? safeConfig.default?.pre ?? [],
      post: override?.post ?? safeConfig.default?.post ?? [],
    }
  }

  const activeConfig = getActiveConfig()

  const updateComponents = useCallback(
    (position: 'pre' | 'post', components: ComponentConfigV2[]) => {
      const resequenced = components.map((c, idx) => ({ ...c, sortOrder: idx }))
      if (activeBranch === 'default') {
        onChange({
          ...safeConfig,
          default: { ...safeConfig.default, [position]: resequenced },
        })
      } else {
        const overrides = (safeConfig.overrides ?? []).map((o) =>
          o.contentId === activeBranch ? { ...o, [position]: resequenced } : o
        )
        onChange({ ...safeConfig, overrides })
      }
    },
    [safeConfig, activeBranch, onChange]
  )

  const handleDragEnd = (position: 'pre' | 'post') => (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const items = position === 'pre' ? activeConfig.pre : activeConfig.post
    const oldIndex = items.findIndex((c) => c.id === active.id)
    const newIndex = items.findIndex((c) => c.id === over.id)
    updateComponents(position, arrayMove(items, oldIndex, newIndex))
  }

  const handleAdd = (position: 'pre' | 'post') => (component: ComponentConfigV2) => {
    const items = position === 'pre' ? activeConfig.pre : activeConfig.post
    updateComponents(position, [...items, component])
  }

  const handleUpdate = (position: 'pre' | 'post', index: number, updated: ComponentConfigV2) => {
    const items = position === 'pre' ? [...activeConfig.pre] : [...activeConfig.post]
    items[index] = updated
    updateComponents(position, items)
  }

  const handleRemove = (position: 'pre' | 'post', index: number) => {
    const items = position === 'pre' ? [...activeConfig.pre] : [...activeConfig.post]
    items.splice(index, 1)
    updateComponents(position, items)
  }

  // G9 — course 레벨 오프라인 교재 토글(체크업북/워크북). 미설정 시 둘 다 노출(true, 엣지1).
  const isCourse = level === 'Course'
  const materials = safeConfig.offlineMaterials ?? createDefaultOfflineMaterials()

  const renderSection = (position: 'pre' | 'post', label: string, colorClass: string) => {
    const items = position === 'pre' ? activeConfig.pre : activeConfig.post
    return (
      <div className="space-y-2">
        <div className={`inline-block px-2 py-1 rounded text-xs font-bold ${colorClass}`}>
          {position === 'pre' ? '▶' : '■'} {label}
        </div>
        <div className="ml-3 space-y-2">
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd(position)}>
            <SortableContext items={items.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              {items.map((comp, idx) => (
                <ComponentCard
                  key={comp.id}
                  component={comp}
                  onUpdate={(updated) => handleUpdate(position, idx, updated)}
                  onRemove={() => handleRemove(position, idx)}
                  renderOptionsEditor={renderOptionsEditor}
                />
              ))}
            </SortableContext>
          </DndContext>
          <ComponentAdder onAdd={handleAdd(position)} existingCount={items.length} />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold">{level} 레벨</span>
        <BranchSelector
          belongsTo={safeConfig.belongsTo}
          onBelongsToChange={(lvl) => onChange({ ...safeConfig, belongsTo: lvl })}
          overrideNames={(safeConfig.overrides ?? []).map((o) => ({
            contentId: o.contentId,
            contentName: o.contentName,
          }))}
          activeBranch={activeBranch}
          onBranchChange={setActiveBranch}
          allowedLevels={allowedBranchLevels}
        />
      </div>

      {isCourse ? (
        <OfflineMaterialsSettings
          materials={materials}
          onChange={(offlineMaterials) => {
            onChange({ ...safeConfig, offlineMaterials })
          }}
        />
      ) : null}

      {renderSection('pre', 'Pre-Activities (시작 활동)', 'bg-purple-100 text-purple-700')}
      {renderSection('post', 'Post-Activities (종료 활동)', 'bg-yellow-100 text-orange-700')}
    </div>
  )
}
